import React, { useState, useEffect } from 'react';
import { Users, MessageSquare, Info } from 'lucide-react';
import { Family } from '../types';
import { appConfig } from '../config';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  families: Family[];
  currentFamilyId: string | null;
  onSelectFamily: (familyId: string) => void;
}

interface SystemInfo {
  api_version: string;
  ai_provider: string;
  ai_model: string;
  metrics_enabled: boolean;
  environment: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  families,
  currentFamilyId,
  onSelectFamily,
}) => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [uiVersion, setUiVersion] = useState<string>('unknown');

  useEffect(() => {
    // Fetch system info from API
    const fetchSystemInfo = async () => {
      try {
        const response = await fetch(`${appConfig.API_BASE}/system/info`);
        if (response.ok) {
          const data = await response.json();
          setSystemInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch system info:', error);
      }
    };

    // Get UI version from package.json or environment
    setUiVersion(import.meta.env.VITE_APP_VERSION || 'dev');

    fetchSystemInfo();
  }, []);

  const handleFamilyClick = (familyId: string) => {
    onSelectFamily(familyId);
    onClose();
  };

  const getFamilyDisplayName = (family: Family) => {
    return family.parent_name || `Family ${family.id.slice(0, 6)}`;
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-section">
          <h3 className="sidebar-section-title">Families</h3>
          <div className="sidebar-family-list">
            {families.map((family) => (
              <button
                key={family.id}
                className={`sidebar-family-item ${currentFamilyId === family.id ? 'active' : ''}`}
                onClick={() => handleFamilyClick(family.id)}
              >
                <div className="sidebar-family-icon">
                  <Users size={16} />
                </div>
                <div className="sidebar-family-info">
                  <span className="sidebar-family-name">{getFamilyDisplayName(family)}</span>
                  <div className="sidebar-family-stats">
                    <span className="sidebar-stat">
                      <Users size={12} />
                      {family.children_count || 0} kids
                    </span>
                    <span className="sidebar-stat">
                      <MessageSquare size={12} />
                      {family.message_count || 0}
                    </span>
                  </div>
                </div>
              </button>
            ))}
            {families.length === 0 && (
              <p className="sidebar-empty">No families found</p>
            )}
          </div>
        </div>

        {/* Version Info Section */}
        <div className="sidebar-version-info">
          <div className="version-info-badge">
            <div className="version-info-content">
              <div className="version-item">
                <span className="version-label">UI</span>
                <span className="version-value">{uiVersion}</span>
              </div>
              {systemInfo && (
                <>
                  <div className="version-separator" />
                  <div className="version-item">
                    <span className="version-label">API</span>
                    <span className="version-value">{systemInfo.api_version}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
